import { Box, Container, Divider, Stack, Typography } from '@montugroup/design-system';
import type { alternaleaf, leafio, uMeds } from '@montugroup/themes';
import { BrandThemeProvider, montu } from '@montugroup/themes';
import { styled } from '@mui/material';
import type { PropsWithChildren, ReactNode } from 'react';

const ContentLayout = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
  margin: `0 ${theme.spacing(6)}`,
  minHeight: '100%',
}));

const InnerContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

export type AdminPageProps = {
  title: string;
  themeVariant?: typeof alternaleaf | typeof uMeds | typeof montu | typeof leafio | undefined;
  fullWidth?: boolean;
  subtitle?: ReactNode;
  BackLink?: ReactNode;
  TitleAdornment?: ReactNode;
  SubTitleAdornment?: ReactNode;
  Actions?: ReactNode;
  testId?: string;
};

function AdminPageLayout({
  title,
  themeVariant = montu,
  fullWidth = true,
  children,
  subtitle,
  BackLink,
  TitleAdornment,
  SubTitleAdornment,
  Actions,
  testId,
}: PropsWithChildren<AdminPageProps>) {
  return (
    <BrandThemeProvider theme={themeVariant}>
      <Container
        maxWidth={fullWidth ? false : undefined}
        disableGutters={true}
        sx={{ maxWidth: fullWidth ? '100%' : 1100 }}
        {...(testId ? { 'data-testid': testId } : {})}
      >
        <ContentLayout>
          <InnerContainer>
            <Stack gap={2} width={'100%'}>
              {BackLink && <div>{BackLink}</div>}
              <Stack gap={4} direction="row">
                <Typography component="h2" variant="h5">
                  {title}
                </Typography>
                {TitleAdornment && <div>{TitleAdornment}</div>}
              </Stack>

              {subtitle && (
                <Stack gap={4} direction="row" sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    variant="body1"
                    sx={(theme) => ({ color: theme.palette.primary.main, fontWeight: 'bold' })}
                  >
                    {subtitle}
                  </Typography>
                  {SubTitleAdornment && <div>{SubTitleAdornment}</div>}
                </Stack>
              )}
              <Divider />
            </Stack>
            {Actions && <div>{Actions}</div>}
          </InnerContainer>
          {children}
        </ContentLayout>
      </Container>
    </BrandThemeProvider>
  );
}

export default AdminPageLayout;
